/*
 * Copyright (C) 2022 Christian Jean.
 * All rights reserved.
 *
 * CONFIDENTIAL AND PROPRIETARY INFORMATION!
 *
 * Disclosure or use in part or in whole without prior written consent
 * constitutes an infringement of copyright laws which may be punishable
 * by law.
 *
 * THIS SOFTWARE IS PROVIDED "AS IS" AND ANY EXPRESSED OR IMPLIED WARRANTIES
 * INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY
 * AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED.  IN NO EVENT SHALL
 * THE LICENSOR OR ITS CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT,
 * INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT
 * NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES; LOSS OF USE,
 * DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY
 * THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT
 * (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF
 * THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
 */

/////////////////////////////////////////////////////////////////////////////

/**
 * --------------------------------------------------------------------------
 * W A R N I N G
 * --------------------------------------------------------------------------
 * Please do NOT make any modifications to this source code.
 *
 * Changes MUST be made within the 'Container Service' of the
 * 'LUKE Node Services' GitHub repository.
 *
 * Nuvoola/luke-node-services/tree/main/container-service/modules/container
 *
 * All inquiries should be directed to 'Christian Jean'.
 *
 * --------------------------------------------------------------------------
 * U S E F U L  L I N K S
 * --------------------------------------------------------------------------
 *
 * https://en.wikipedia.org/wiki/ISO_6346
 * http://www.shippingline.org/container-numbers/
 * https://www.bic-code.org/bic-codes/
 * https://www.bic-code.org/container-bic-code/ABCU/
 */

/////////////////////////////////////////////////////////////////////////////

var ContainerService = (function () {
   const MODULE_NAME = 'ContainerService';
   const MODULE_VERSION = '1.2.0';

   const MAPPING = {};

   const LETTERS = '0123456789abcdefghijklmnopqrstuvwxyz';

   //--------------------------------------------------------
   // Container Identification Number
   //
   // ISO 6346 standard (see: en.wikipedia.org/wiki/ISO_6346)
   //
   // Part 1 - Owner code (BIC), 3-digits (A-Z)
   // Part 2 - Category identifier, 1-digit (U, J, or Z)
   // Part 3 - Serial number, 6-digits (0-9)
   // Part 4 - Check digit, 1-digit (0-9)
   //--------------------------------------------------------
   const ISO_CODE_10_DIGITS = /^[a-zA-Z]{3}[uUjJzZ]{1}(\s*)?[0-9]{6}$/;
   const ISO_CODE_11_DIGITS = /^[a-zA-Z]{3}[uUjJzZ]{1}(\s*)?[0-9]{6}(\s*)?[0-9]{1}$/;

   //--------------------------------------------------------
   // Container Size and Type Code (SATC).
   //
   // Note that this only validates ISO codes (not pre 1984)
   //
   // ISO 6346 standard (see: en.wikipedia.org/wiki/ISO_6346)
   // To validate (see: www.bic-code.org/size-type-code)
   //
   // Part 1 - Is 1-digit identifying the length (1,2,3,4,B,C,G,H,L,M,N).
   // Part 2 - Is 1-digit identifying the height (0,2,4,5,6,8,9,C,D,E,F).
   // Part 3 - Is 2-digits identifying the characteristics.
   //          -> Possibility of 10 letters (G,V,B,S,R,H,U,P,T,A).
   //          -> Possibility of up to 9 digits (0-9).
   //--------------------------------------------------------
   const SATC = /^[1234BCGHLMN][0245689CDEF](G[0-3])|(V[024])|(B[013456])|(S[0-2])|(R[0-3])|(H[01256])|(U[0-5])|(P[0-5])|(T[0-9])|(A0)$/;

   //-------------------------------------------------------------------
   // Constructor...
   //-------------------------------------------------------------------

   init.apply(null, arguments);

   //////////////////////////////////////////////////////////////////////////
   // PRIVATE METHODS
   //////////////////////////////////////////////////////////////////////////

   /**
    * Initialize our module.
    */
   function init() {
      console.log("Initializing '" + MODULE_NAME + "' instance");
      buildValueMap();
   }

   /**
    * Will construct a map of all values for each possible
    * digit of a Container ID (digits a-z, 0-9).
    */
   function buildValueMap() {
      for (let i = 0, v = 0; i < LETTERS.length; i++, v++) {
         if (v > 0 && v % 11 == 0) v++;
         MAPPING[LETTERS[i]] = v;
      }
   }

   /**
    * Will validate that 'str' is in fact a string of at lease one character
    * in length.
    *
    * @param {String} str to validate
    * @returns 'true' if a string of at least one character, 'false' otherwise.
    */
   function isValidString(str) {
      return str && typeof str === 'string' && str.length > 0;
   }

   //////////////////////////////////////////////////////////////////////////
   // CONTAINER ID FUNCTIONALITY
   //////////////////////////////////////////////////////////////////////////

   /**
    * Will validate that the container ID provided is in fact a valid
    * and properly structured container ID, for either:
    *
    *  a) A 10-digit container ID (with no check-digit)
    *  b) An 11-digit container ID (with a check-digit)
    *
    * The container ID may contain optional whitespace characters.
    *
    * @param {String} containerID a valid 10-digit or 11-digit container ID.
    *
    * @returns 'true' if valid, 'false' otherwise.
    */
   function isValidContainerID(containerID) {
      return containerID ? containerID.match(ISO_CODE_10_DIGITS) || containerID.match(ISO_CODE_11_DIGITS) : false;
   }

   /**
    * Will validate that a container ID check-digit, by one of two methods:
    *
    * 1. Against an 11-digit container ID, using the 11th character as
    *    the check digit (second parameter is ignored).
    *
    * 2. Against a 10-digit container ID where the 'checkDigit' is provided
    *    as a second parameter.
    *
    * @param {String} containerId is the Container ID code.
    * @param {Number} checkDigit is a an opttional check digit (from 0 to 9).
    *
    * @returns A boolean 'true' if the container ID passes a check digit validation.
    *          A boolean 'false' if the container ID deoesn't pass a check digit validation.
    *          A 'null' value if the container ID provided is not valid.
    */
   function validateCheckDigit(containerId, checkDigit) {
      let checksum = null;
      let valid = false;

      if (!isValidString(containerId)) return null;

      containerId = containerId.toLowerCase();
      containerId = containerId.replace(/\s/g, '');

      if (containerId.match(ISO_CODE_11_DIGITS)) {
         checkDigit = containerId.substring(10, 11);
         containerId = containerId.substring(0, 10);
         valid = true;
      }

      if (containerId.match(ISO_CODE_10_DIGITS)) {
         checksum = calculateCheckDigit(containerId);
         valid = true;
      }

      if (!valid) return null;

      // Do not use '===' to compare!
      // We don't type check 'checkDigit', which
      // could be 'null', a symbol or a letter.
      return checkDigit == checksum;
   }

   /**
    * Will caluclate a container ID's check-digit (11th digit).
    *
    * The input ('containerId') must match that of the 'ISO 6346' regex
    * validation (having at least 10-digits), otherwise a 'null' value
    * will be returned.
    *
    * @param {String} containerId is the Container ID code (w/ 10-digits).
    *
    * @returns A check-digit value from 0 to 9 (Number), or null if input
    *          provided was invalid.
    */
   function calculateCheckDigit(containerId) {
      let checkDigit = null;
      let sum = 0;

      if (!isValidString(containerId)) return null;

      containerId = containerId.toLowerCase();
      containerId = containerId.replace(/\s/g, '');

      if (containerId.match(ISO_CODE_10_DIGITS)) {
         let pos = 1;

         for (let i = 0; i < containerId.length; i++) {
            sum += MAPPING[containerId[i]] * pos;
            pos = pos << 1;
         }

         checkDigit = sum - Math.floor(sum / 11) * 11;

         /**
          * If the final difference is 10, then the check digit becomes 0.
          * To ensure that this does not happen the standard recommends that
          * serial numbers should not be used which produce a final difference
          * of 10; however, there are containers in the market which do not
          * follow this recommendation, so handling this case has to be
          * included if a check digit calculator is programmed.
          *
          * See 'Calculation step 3' at https://en.wikipedia.org/wiki/ISO_6346
          */
         if (checkDigit > 9) checkDigit = 0;
      }

      return checkDigit === null ? null : parseInt(checkDigit);
   }

   /**
    * Extract the 3-character BIC (Bureau of International Containers)
    * from the container ID provided.
    *
    * Example:
    *
    *    Provided with 'CSQU 123456 9', will return 'CSQ'.
    *
    * @param {String} containerId is the Container ID code.
    *
    * @returns a three (3) character string, or 'null' if 'containerId'
    *          is invalid.
    */
   function getBIC(containerId) {
      let bic = null;

      if (isValidContainerID(containerId)) {
         bic = containerId.replace(/\s/g, '').slice(0, 3);
      }

      return bic;
   }

   /**
    * Extract the 1-character category (U, J or Z) from the
    * container ID provided.
    *
    * Example:
    *
    *    Provided with 'CSQU 123456 9', will return 'U'.
    *
    * @param {String} containerId is the Container ID code.
    *
    * @returns a one (1) character string, or 'null'
    *          if 'containerId' is invalid.
    */
   function getCategory(containerId) {
      let category = null;

      if (isValidContainerID(containerId)) {
         category = containerId.replace(/\s/g, '').slice(3, 4).toUpperCase();
      }

      return category;
   }

   /**
    * Extract the 6-digit serial number (0-9) from the container ID
    * provided.
    *
    * Example:
    *
    *    Provided with 'CSQU 123456 9', will return '123456'.
    *
    * @param {String} containerId is the Container ID code.
    *
    * @returns a six (6) digit string (0-9), or 'null' if 'containerId'
    *          is invalid.
    */
   function getSerialNumber(containerId) {
      let serial = null;

      if (isValidContainerID(containerId)) {
         serial = containerId.replace(/\s/g, '').slice(4, 10);
      }

      return serial;
   }

   /**
    * Extract the 1-digit check digit from the container ID provided.
    *
    * If the container ID has 10-digits, the checksum will automatically
    * be calculated and returned.
    *
    * If the container ID has 11-digits, the checksum will be extracted
    * and returned (but not validated).
    *
    * Example:
    *
    *    Provided with 'CSQU 123456 9', will return '9'.
    *
    * @param {String} containerId is the Container ID code.
    *
    * @returns a one (1) digit checksum number (0-9), or 'null' if
    *          'containerId' is invalid.
    */
   function getCheckDigit(containerId) {
      let checksum = null;

      if (containerId.match(ISO_CODE_11_DIGITS)) {
         checksum = containerId.replace(/\s/g, '').slice(-1);
      }

      if (containerId.match(ISO_CODE_10_DIGITS)) {
         checksum = calculateCheckDigit(containerId);
      }

      return checksum;
   }

   /**
    * Extract the entire container ID, except a 1-digit check digit if
    * it was available.
    *
    * Example:
    *
    *    Provided with 'CSQU 123456 9', will return 'CSQU 123456'.
    *
    * @param {String} containerId is the Container ID code.
    *
    * @returns a one (1) digit checksum number (0-9), or 'null' if
    *          'containerId' is invalid.
    */
   function getAllButCheckDigit(containerId) {
      let all = null;

      if (isValidContainerID(containerId)) {
         all = containerId.replace(/\s/g, '').slice(0, 10);
      }

      return all;
   }

   /**
    * Determine if the container ID's category is of type 'freight' (U).
    */
   function isFreightContainer(containerId) {
      let cat = getCategory(containerId);
      return cat === 'U';
   }

   /**
    * Determine if the container ID's category is of type 'detachable freight' (J).
    */
   function isDetachableFreight(containerId) {
      let cat = getCategory(containerId);
      return cat === 'J';
   }

   /**
    * Determine if the container ID's category is of type 'trailer or chasis' (Z).
    */
   function isTrailerOrChasis(containerId) {
      let cat = getCategory(containerId);
      return cat === 'Z';
   }

   /**
    * Will normalize a container ID string, by:
    *
    *  1. Ensuring that it is valid (10 or 11 digits).
    *  2. Calculating a check-digit, if one is not available.
    *  3. Proper spacing (remove duplicate spaces, and optionally add some).
    *  4. Ensure it is all in capital letters (uppercase).
    *
    * @param {String} containerId is the Container ID code.
    * @param {Boolean} space will insert spaces (default is 'false')
    *
    * @returns a container ID string.
    */
   function normalize(containerId, space) {
      let cid = null;

      if (isValidContainerID(containerId)) {
         let bic = getBIC(containerId);
         let cat = getCategory(containerId);
         let ser = getSerialNumber(containerId);
         let chk = getCheckDigit(containerId);

         let delim = arguments.length > 1 && typeof space == 'boolean' && space ? ' ' : '';

         cid = (bic + cat + delim + ser + delim + chk).toUpperCase();
      }

      return cid;
   }

   //////////////////////////////////////////////////////////////////////////
   // CONTAINER SATC FUNCTIONALITY
   //////////////////////////////////////////////////////////////////////////

   /**
    * Will validate the SATC (container 'Size and Type Code'), for length,
    * structure and proper digits.
    *
    * Note: Function is a bit slow considering parsed results is analyzed
    * versus using the SATC regex.
    *
    * @param {String} satc is a 4-digit container 'Size and Type Code' (SATC).
    *
    * @returns 'true' if valid, 'false' otherwise.
    */
   function isValidSATC(satc) {
      let res = getTypeAndDimentions(satc);
      return res && res.type && res.type.valid && res.dimentions && res.dimentions.length && res.dimentions.width && res.dimentions.height ? true : false;
   }

   /**
    * Will parse the 4-digit container 'Size and Type Code' (SATC) to extract
    * specific information.
    *
    * @param {String} satc is a 4-digit container 'Size and Type Code' (SATC).
    *
    * @returns a JSON object.
    */
   function getTypeAndDimentions(satc) {
      let type = getContainerType(satc);
      let dims = getContainerDimentions(satc);

      return { code: satc, type: type, dimentions: dims };
   }

   /**
    * Will fetch the container measurements, as described by the Containr 'Size and Type Code' (SATC).
    *
    * Is expecting one of the following inputs:
    *
    *    1. A 1-digit string, which will assume is the 1st digit of the full 4-digit SATC.
    *       In this case, the length information will be extracted.
    *
    *    2. A 2-digit string, which will assume is the 1st and 2nd digit of the full 4-digit SATC.
    *       In this case, the length, width and height of the container will be returned.
    *
    * If the input is longer than 2 characters, the remaining will be ignored.
    *
    * @param {String} satc a string having at least 2 characters.
    *
    * @returns a JSON object if successfull, 'null' otherwise. */
   function getContainerDimentions(satc) {
      let digit1 = null;
      let digit2 = null;
      let length = null;
      let width = null;
      let height = null;

      if (!isValidString(satc)) return null;

      // First, process the 1st digit (length)
      if (satc.length >= 1) digit1 = satc.substring(0, 1).toUpperCase();

      switch (digit1) {
         case '1':
            length = { ft: 10, in: 120, mm: 3048, cm: 304.8, m: 3.048 };
            break;
         case '2':
            length = { ft: 20, in: 240, mm: 6096, cm: 609.6, m: 6.096 };
            break;
         case '3':
            length = { ft: 30, in: 360, mm: 9144, cm: 914.4, m: 9.144 };
            break;
         case '4':
            length = { ft: 40, in: 480, mm: 12192, cm: 1219.2, m: 12.192 };
            break;
         case 'B':
            length = { ft: 24, in: 288, mm: 7315, cm: 731.5, m: 7.315 };
            break;
         case 'C':
            length = { ft: 24.5, in: 294, mm: 7468, cm: 746.8, m: 7.468 };
            break;
         case 'G':
            length = { ft: 41, in: 492, mm: 12497, cm: 1249.7, m: 12.497 };
            break;
         case 'H':
            length = { ft: 43, in: 516, mm: 13106, cm: 1310.6, m: 13.106 };
            break;
         case 'L':
            length = { ft: 45, in: 540, mm: 13716, cm: 1371.6, m: 13.716 };
            break;
         case 'M':
            length = { ft: 48, in: 576, mm: 14630, cm: 1463.0, m: 14.63 };
            break;
         case 'N':
            length = { ft: 49, in: 588, mm: 14935, cm: 1493.5, m: 14.935 };
            break;
      }

      // Next, process the second digit (width and height)
      if (satc.length >= 2) digit2 = satc.substring(1, 2).toUpperCase();

      switch (digit2) {
         case '0':
            height = { ft: 8, in: 96, mm: 2438, cm: 243.8, m: 2.438 };
            width = { ft: 8, in: 96, mm: 2438, cm: 243.8, m: 2.438 };
            break;
         case '2':
            height = { ft: 8.5, in: 102, mm: 2590.8, cm: 259.08, m: 2.5908 };
            width = { ft: 8, in: 96, mm: 2438, cm: 243.8, m: 2.438 };
            break;
         case '4':
            height = { ft: 9, in: 108, mm: 2743.2, cm: 274.32, cm: 2.7432 };
            width = { ft: 8, in: 96, mm: 2438, cm: 243.8, m: 2.438 };
            break;
         case '5':
            height = { ft: 9.5, in: 114, mm: 2895.6, cm: 289.56, m: 2.896 };
            width = { ft: 8, in: 96, mm: 2438, cm: 243.8, m: 2.438 };
            break;
         case '6':
            height = { ft: 9.5, in: 114, mm: 2895.6, cm: 289.56, m: 2.896, note: '> 9 ft 6 in' };
            width = { ft: 8, in: 96, mm: 2438, cm: 243.8, m: 2.438 };
            break;
         case '8':
            height = { ft: 4.25, in: 51, mm: 1295.4, cm: 129.54, m: 1.2954 };
            width = { ft: 8, in: 96, mm: 2438, cm: 243.8, m: 2.438 };
            break;
         case '9':
            height = { ft: 4, in: 48, mm: 1219.2, cm: 121.92, m: 1.2192, note: '<= 4 ft' };
            width = { ft: 0, in: 0, mm: 0, cm: 0, m: 0, note: 'Unknown' }; // Can't find data!
            break;
         case 'C':
            height = { ft: 8.5, in: 102, mm: 2590.8, cm: 259.08, m: 2.5908 };
            width = { ft: 8, in: 96, mm: 2438, cm: 243.8, m: 2.438, note: '8 ft (2,438 mm) < X <= 8 ft 2 in (2,500 mm)' };
            break;
         case 'D':
            height = { ft: 9, in: 108, mm: 2743.2, cm: 274.32, m: 2.7432 };
            width = { ft: 8, in: 96, mm: 2438, cm: 243.8, m: 2.438, note: '8 ft (2,438 mm) < X <= 8 ft 2 in (2,500 mm)' };
            break;
         case 'E':
            height = { ft: 9.5, in: 114, mm: 2895.6, cm: 289.56, m: 2.896 };
            width = { ft: 8, in: 96, mm: 2438, cm: 243.8, m: 2.438, note: '8 ft (2,438 mm) < X <= 8 ft 2 in (2,500 mm)' };
            break;
         case 'F':
            height = { ft: 9.5, in: 114, mm: 2895.6, cm: 289.56, m: 2.896, note: '> 9.5 ft' };
            width = { ft: 8, in: 96, mm: 2438, cm: 243.8, m: 2.438, note: '8 ft (2,438 mm) < X <= 8 ft 2 in (2,500 mm)' };
            break;
      }

      return { length: length, width: width, height: height };
   }

   /**
    * Will fetch the container type, from the Containr 'Size and Type Code' (SATC).
    *
    * Is expecting one of the following inputs:
    *
    *    1. A 2-digit string, which will assume is the 3rd and 4th digits of the full 4-digit SATC.
    *    2. A 4-digit string, which will assume is the full 4-digit SATC.
    *
    * If the input is longer than 4 characters, the remaining will be ignored.
    *
    * Before the ISO reached an agreement on the marking codes in 1995, the markings were not set
    * in stone so there were a lot more codes than necessary. There is a discrepancy between
    * 1984 codes and 1995 ones. We support 13 1984 'numeric' codes (00, 10, 15, 30, 32, 54, 50,
    * 51, 60, 63, 70, 75, 80).
    *
    * See: https://www.csiu.co/resources-and-links/iso-container-size-and-type-iso-6346
    *
    * @param {String} satc a string having at least 2 characters.
    *
    * @returns a JSON object if successfull, 'null' otherwise.
    */
   function getContainerType(satc) {
      const DEFAULT_ERROR_MESSAGE = 'Invalid or empty container code';
      let type = null;
      let json = { valid: true, iso: true, 1984: false };

      if (!isValidString(satc)) return { ...json, valid: false, code: satc, iso: false, error: DEFAULT_ERROR_MESSAGE };

      if (satc.length == 2) type = satc.toUpperCase();
      if (satc.length >= 4) type = satc.substring(2, 4).toUpperCase();

      switch (type) {
         // G - General Purpose container without ventilation (0, 1, 2, 3)
         case '00':
            json = { ...json, iso: false, 1984: true, equiv: 'G0' };
         case 'G0':
            json = { ...json, code: type, category: 'General purpose, without ventilation', desc: 'Openings at one or both ends' };
            break;
         case '10':
            json = { ...json, iso: false, 1984: true, equiv: 'G1' };
         case 'G1':
            json = { ...json, code: type, category: 'General purpose, without ventilation', desc: 'Passive vents at upper part of cargo space' };
            break;
         case '54':
            json = { ...json, iso: false, 1984: true, equiv: 'G2' };
         case 'G2':
            json = { ...json, code: type, category: 'General purpose, without ventilation', desc: 'Opening(s) at one or both ends plus "full" opening(s) on one or both sides' };
            break;
         case 'G3':
            json = { ...json, code: type, category: 'General purpose, without ventilation', desc: 'Opening(s) at one or both ends plus "partial" opening(s) on one or both sides' };
            break;

         // V - General purpose container with ventilation (0, 2, 4)
         case 'V0':
            json = { ...json, code: type, category: 'General purpose, with ventilation (Fantainer / Highly Ventilated)', desc: 'Non-mechanical, vents at lower and upper parts of cargo space' };
            break;
         case '15':
            json = { ...json, iso: false, 1984: true, equiv: 'V1' };
         case 'V1':
            json = { ...json, code: type, category: 'General purpose, with ventilation (Fantainer / Highly Ventilated)', desc: 'Mechanical ventilation system located internally' };
            break;
         case 'V2':
            json = { ...json, code: type, category: 'General purpose, with ventilation (Fantainer / Highly Ventilated)', desc: 'Mechanical ventilation system located internally' };
            break;
         case 'V4':
            json = { ...json, code: type, category: 'General purpose, with ventilation (Fantainer / Highly Ventilated)', desc: 'Mechanical ventilation system located externally' };
            break;

         // B - Dry bulk container (0, 1, 3, 4, 5, 6)
         case '80':
            json = { ...json, iso: false, 1984: true, equiv: 'B0' };
         case 'B0':
            json = { ...json, code: type, category: 'Bulk', desc: 'Non-presurized, box type, closed' };
            break;
         case 'B1':
            json = { ...json, code: type, category: 'Bulk', desc: 'Non-presurized, box type, airtight' };
            break;
         case 'B3':
            json = { ...json, code: type, category: 'Bulk', desc: 'Pressurized, horizontal discharge, test pressure 150 kPa' };
            break;
         case 'B4':
            json = { ...json, code: type, category: 'Bulk', desc: 'Pressurized, horizontal discharge, test pressure 265 kPa' };
            break;
         case 'B5':
            json = { ...json, code: type, category: 'Bulk', desc: 'Pressurized, tipping discharge, test pressure 150 kPa' };
            break;
         case 'B6':
            json = { ...json, code: type, category: 'Bulk', desc: 'Pressurized, tipping discharge, test pressure 265 kPa' };
            break;

         // S - Named cargo container (0, 1, 2)
         case 'S0':
            json = { ...json, code: type, category: 'Named cargo container', desc: 'Livestock carrier' };
            break;
         case 'S1':
            json = { ...json, code: type, category: 'Named cargo container', desc: 'Automobile carrier' };
            break;
         case 'S2':
            json = { ...json, code: type, category: 'Named cargo container', desc: 'Live fish carrier' };
            break;

         // R - Thermal container (0, 1, 2, 3)
         case '30':
            json = { ...json, iso: false, 1984: true, equiv: 'R0' };
         case 'R0':
            json = { ...json, code: type, category: 'Integral Reefer', desc: 'Mechanically refrigerated' };
            break;
         case '32':
            json = { ...json, iso: false, 1984: true, equiv: 'R1' };
         case 'R1':
            json = { ...json, code: type, category: 'Integral Reefer', desc: 'Mechanically refrigerated and heated' };
            break;
         case 'R2':
            json = { ...json, code: type, category: 'Integral Refer', desc: 'Self-powered refrigerated/heated, mechanically refrigerated' };
            break;
         case 'R3':
            json = { ...json, code: type, category: 'Integral Refer', desc: 'Mechanically refrigerated and heated' };
            break;

         // H - Thermal container (0, 1, 2, 5, 6)
         case 'H0':
            json = {
               ...json,
               code: type,
               category: 'Refrigerated and/or heated',
               desc: 'Refrigerated and/or heated, with removable equipment located externally; heat transfer coefficient K=0.4W/M2.K',
            };
            break;
         case 'H1':
            json = { ...json, code: type, category: 'Refrigerated and/or heated', desc: 'Refrigerated and/or heated with removable equipment located internally' };
            break;
         case 'H2':
            json = {
               ...json,
               code: type,
               category: 'Refrigerated and/or heated',
               desc: 'Refrigerated and/or heated with removable equipment located externally; heat transfer coefficient K=0.7W/M2.K',
            };
            break;
         case 'H5':
            json = { ...json, code: type, category: 'Insulated', desc: 'Heat transfer coefficient K=0.4W/M2.K' };
            break;
         case 'H6':
            json = { ...json, code: type, category: 'Insulated', desc: 'Heat transfer coefficient K=0.7W/M2.K' };
            break;

         // U - Open-top container (0, 1, 2, 3, 4, 5)
         case '50':
            json = { ...json, iso: false, 1984: true, equiv: 'U0' };
         case 'U0':
            json = { ...json, code: type, category: 'Open Top', desc: 'Opening(s) at one or both ends' };
            break;
         case '51':
            json = { ...json, iso: false, 1984: true, equiv: 'U1' };
         case 'U1':
            json = { ...json, code: type, category: 'Open Top', desc: 'Opening(s) at one or both ends plus removable top member(s) in end frame(s)' };
            break;
         case 'U2':
            json = { ...json, code: type, category: 'Open Top', desc: 'Opening(s) at one or both ends, plus opening(s) on one or both sides' };
            break;
         case 'U3':
            json = { ...json, code: type, category: 'Open Top', desc: 'Opening(s) at one or both ends, plus opening(s) on one or both  sides plus removable top  member(s) in end frame(s)' };
            break;
         case 'U4':
            json = { ...json, code: type, category: 'Open Top', desc: 'Opening(s) at one or both ends, plus partial opening on one side and full opening on the other side' };
            break;
         case 'U5':
            json = { ...json, code: type, category: 'Open Top', desc: 'Complete, fixed side and end walls (no doors)' };
            break;

         // P - Platform container (0, 1, 2, 3, 4, 5)
         case '60':
            json = { ...json, iso: false, 1984: true, equiv: 'P0' };
         case 'P0':
            json = { ...json, code: type, category: 'Platform', desc: 'Platform (container)' };
            break;
         case '63':
            json = { ...json, iso: false, 1984: true, equiv: 'P1' };
         case 'P1':
            json = { ...json, code: type, category: 'Platform', desc: 'Platform-based containers with incomplete superstructure - Fixed, two complete and fixed ends' };
            break;
         case 'P2':
            json = {
               ...json,
               code: type,
               category: 'Platform',
               desc: 'Platform-based containers with incomplete superstructure - Fixed, fixed posts, either free-standing or with removable top member',
            };
            break;
         case 'P3':
            json = { ...json, code: type, category: 'Platform', desc: 'Platform-based containers with incomplete superstructure - Folding (collapsible), folding complete end structure' };
            break;
         case 'P4':
            json = {
               ...json,
               code: type,
               category: 'Platform',
               desc: 'Platform-based containers with incomplete superstructure - Folding (collapsible), Folding posts, either free-standing or with removable top member',
            };
            break;
         case 'P5':
            json = { ...json, code: type, category: 'Platform', desc: 'Platform-based containers with complete super structure - Open top, open ends (skeletal)' };
            break;

         // T - Tank container (0, 1, 2, 3, 4, 5, 6, 7, 8, 9)
         case '70':
            json = { ...json, iso: false, 1984: true, equiv: 'T0' };
         case 'T0':
            json = { ...json, code: type, category: 'Tank', desc: 'Non-dangerous liquids, minimum pressure (0.45 bar / 45 kPa / 6.527 PSI)' };
            break;
         case 'T1':
            json = { ...json, code: type, category: 'Tank', desc: 'Non-dangerous liquids, minimum pressure (1.50 bar / 150 kPa / 21.756 PSI)' };
            break;
         case 'T2':
            json = { ...json, code: type, category: 'Tank', desc: 'Non-dangerous liquids, minimum pressure (2.65 bar / 265 kPa / 38.435 PSI)' };
            break;
         case 'T3':
            json = { ...json, code: type, category: 'Tank', desc: 'Dangerous liquids, minimum pressure (1.50 bar / 150 kPa / 21.756 PSI)' };
            break;
         case 'T4':
            json = { ...json, code: type, category: 'Tank', desc: 'Dangerous liquids, minimum pressure (2.65 bar / 265 kPa / 38.435 PSI)' };
            break;
         case '75':
            json = { ...json, iso: false, 1984: true, equiv: 'T5' };
         case 'T5':
            json = { ...json, code: type, category: 'Tank', desc: 'Dangerous liquids, minimum pressure (4.00 bar / 400 kPa / 58.015 PSI)' };
            break;
         case 'T6':
            json = { ...json, code: type, category: 'Tank', desc: 'Dangerous liquids, minimum pressure (6.00 bar / 600 kPa / 87.023 PSI)' };
            break;
         case 'T7':
            json = { ...json, code: type, category: 'Tank', desc: 'Gases, minimum pressure (9.10 bar / 910 kPa / 131.984 PSI)' };
            break;
         case 'T8':
            json = { ...json, code: type, category: 'Tank', desc: 'Gases, minimum pressure (22.00 bar / 2200 kPa / 319.083 PSI)' };
            break;
         case 'T9':
            json = { ...json, code: type, category: 'Tank', desc: 'Gases, minimum pressure to be decided' };
            break;

         // A - Air/surface container (0)
         case 'A0':
            json = { ...json, code: type, category: 'Air/surface container', desc: 'Air/surface container - A0' };
            break;

         default:
            json = {
               ...json,
               code: type,
               valid: false,
               error: DEFAULT_ERROR_MESSAGE,
               iso: false,
               1984: false,
            };
            break;
      }

      return json;
   }

   /**
    * Provides the version of this module.
    */
   function getVersion() {
      return MODULE_VERSION;
   }

   /**
    * String representation of this instance.
    */
   function toString() {
      var status = [];

      status.push(
         JSON.stringify({
            Version: getVersion(),
         })
      );

      var text = MODULE_NAME + ': [' + status.join(', ') + ']';

      return text;
   }

   //-------------------------------------------------------------------
   // Exported Methods...
   //-------------------------------------------------------------------

   return {
      // Container ID
      isValidContainerID: isValidContainerID,
      validateCheckDigit: validateCheckDigit,
      calculateCheckDigit: calculateCheckDigit,

      getBIC: getBIC,
      getCategory: getCategory,
      getSerialNumber: getSerialNumber,
      getCheckDigit: getCheckDigit,
      getAllButCheckDigit: getAllButCheckDigit,
      normalize: normalize,

      isFreightContainer: isFreightContainer,
      isDetachableFreight: isDetachableFreight,
      isTrailerOrChasis: isTrailerOrChasis,

      // SATC (size and Type Code)
      isValidSATC: isValidSATC,
      getTypeAndDimentions: getTypeAndDimentions,
      getContainerDimentions: getContainerDimentions,
      getContainerType: getContainerType,

      // Support
      getVersion: getVersion,
      toString: toString,
   };
})(); // Our IIFE function is invoked here

// module.exports = ContainerService;
export default ContainerService;

console.log(ContainerService.toString());

/**
 * These are just some basic function tests (did unit testing elsewhere).
 * Really need to re-organize these tests and add the unit testing.
 * Probably should use Mocha/Chai for this!!
 */
function ContainerServiceTests() {
   let cid = 'CQSU 123456 9';
   // let satc = '22g0'; // all valid
   let satc = '12A0'; // all valid
   // let satc = '02g0'; // invalid length (first digit)
   // let satc = '11g0'; // invalid height/width (second digit)
   // let satc = '12X0'; // invalid type (third digit)
   // let satc = '12A3'; // invalid type (fourth digit)

   let type = ContainerService.getContainerType(satc);
   let dims = ContainerService.getContainerDimentions(satc);
   let json = ContainerService.getTypeAndDimentions(satc); // union of 'type' and 'dims'

   console.log();
   console.log('TYPE: ' + JSON.stringify(type));
   console.log('DIMS: ' + JSON.stringify(dims));
   console.log();
   console.log('JSON: ' + JSON.stringify(json, null, 4));
   console.log('Valid: ' + ContainerService.isValidSATC(satc));

   let bic = ContainerService.getBIC(cid);
   let cat = ContainerService.getCategory(cid);
   let snn = ContainerService.getSerialNumber(cid);
   let sum = ContainerService.getCheckDigit(cid);
   let all = ContainerService.getAllButCheckDigit(cid);

   console.log('Code: ' + satc + "'");
   console.log('Type: ' + JSON.stringify(type));
   console.log('Dims: ' + JSON.stringify(dims));
   console.log('JSON: ' + JSON.stringify(json, null, 4));

   console.log('BIC: ' + bic);
   console.log('CAT: ' + cat);
   console.log('SNN: ' + snn);
   console.log('SNN2 ' + ContainerService.getCheckDigit('abcU 123456'));
   console.log('SUM: ' + sum);
   console.log('ALL: ' + all);
   console.log('NRM1: ' + ContainerService.normalize(cid));
   console.log('NRM2: ' + ContainerService.normalize('abcU 123456 7', true));
   console.log('NRM3: ' + ContainerService.normalize('abcU 123456', true));

   console.log('Valid chk 1: ' + ContainerService.validateCheckDigit('abcu 123456 1'));
   console.log('Valid chk 2: ' + ContainerService.validateCheckDigit('abcu 123456 2'));
   console.log('Valid chk 2: ' + ContainerService.validateCheckDigit('abcu 123456 3'));
   console.log('Valid chk 2: ' + ContainerService.validateCheckDigit('abcu 123456 4'));
   console.log('Valid chk 2: ' + ContainerService.validateCheckDigit('abcu 123456 5'));
   console.log('Valid chk 2: ' + ContainerService.validateCheckDigit('abcu 123456 6'));
   console.log('Valid chk 2: ' + ContainerService.validateCheckDigit('abcu 123456 7'));
   console.log('Valid chk 2: ' + ContainerService.validateCheckDigit('abcu 123456 8'));
   console.log('Valid chk 2: ' + ContainerService.validateCheckDigit('abcu 123456 9'));
   console.log('Valid chk 3: ' + ContainerService.validateCheckDigit('abcu 123456 0'));
   console.log('Valid chk 4: ' + ContainerService.validateCheckDigit('abcu 123456'));
   console.log('Valid chk 4: ' + ContainerService.validateCheckDigit('abcu 12345'));

   console.log('Is Freight container? ' + ContainerService.isFreightContainer('abcx 123456 8'));
   console.log('Is Freight container? ' + ContainerService.isFreightContainer('abcu 123456 8'));

   console.log('Is Detachable Freight ? ' + ContainerService.isDetachableFreight('abcu 123456 8'));
   console.log('Is Detachable Freight ? ' + ContainerService.isDetachableFreight('abcj 123456 8'));

   console.log('Is Trailer or Chasis? ' + ContainerService.isTrailerOrChasis('abcu 123456 8'));
   console.log('Is Trailer or Chasis? ' + ContainerService.isTrailerOrChasis('abcz 123456 8'));
}

// ContainerServiceTests();


